/* eslint-disable @typescript-eslint/no-explicit-any */
"use client";

import React, { useEffect, useRef } from "react";

export interface SplitTextProps {
  text: string;
  className?: string;
  delay?: number;
  duration?: number;
  ease?: string | ((t: number) => number);
  splitType?: "chars" | "words" | "lines" | "words, chars";
  from?: Record<string, unknown>;
  to?: Record<string, unknown>;
  threshold?: number;
  rootMargin?: string;
  textAlign?: React.CSSProperties["textAlign"];
  onLetterAnimationComplete?: () => void;
  as?: any;
  ariaSafe?: boolean;
}

const SplitText: React.FC<SplitTextProps> = ({
  text,
  className = "",
  delay = 100,
  duration = 0.6,
  ease = "power3.out",
  splitType = "chars",
  from = { opacity: 0, y: 40 },
  to = { opacity: 1, y: 0 },
  threshold = 0.1,
  rootMargin = "-100px",
  textAlign = "center",
  onLetterAnimationComplete,
  as: Comp = "p",
  ariaSafe = true,
}) => {
  const ref = useRef<HTMLElement>(null);
  const done = useRef(false);

  useEffect(() => {
    if (typeof window === "undefined" || done.current) return;
    const el = ref.current as HTMLElement | null;
    if (!el) return;

    let gsap: any;
    let ScrollTrigger: any;
    let SplitText: any;
    let splitter: any;
    let targets: HTMLElement[] = [];
    let tl: any;

    (async () => {
      const g = await import("gsap");
      gsap = g.gsap || g.default || g;
      ScrollTrigger = (await import("gsap/ScrollTrigger")).ScrollTrigger;
      SplitText = (await import("gsap/SplitText")).default;

      gsap.registerPlugin(ScrollTrigger, SplitText);

      el.removeAttribute("aria-label");
      el.removeAttribute("role");

      const absoluteLines = splitType === "lines";
      if (absoluteLines) el.style.position = "relative";

      splitter = new SplitText(el, {
        type: splitType,
        absolute: absoluteLines,
        linesClass: "split-line",
      });

      targets =
        splitType === "lines"
          ? splitter.lines
          : splitType === "words"
          ? splitter.words
          : splitType === "words, chars"
          ? [...splitter.words, ...splitter.chars]
          : splitter.chars;

      targets.forEach((t: any) => (t.style.willChange = "transform, opacity"));

      const startPct = (1 - threshold) * 100;
      const m = /^(-?\d+)px$/.exec(rootMargin);
      const raw = m ? parseInt(m[1], 10) : 0;
      const sign = raw < 0 ? `-=${Math.abs(raw)}px` : `+=${raw}px`;
      const start = `top ${startPct}%${sign}`;

      tl = gsap.timeline({
        scrollTrigger: {
          trigger: el,
          start,
          toggleActions: "play none none none",
          once: true,
        },
        smoothChildTiming: true,
        onComplete: () => {
          done.current = true;
          gsap.set(targets, {
            ...to,
            clearProps: "willChange",
            immediateRender: true,
          });
          onLetterAnimationComplete?.();
        },
      });

      tl.set(targets, { ...from, immediateRender: false, force3D: true });
      tl.to(targets, {
        ...to,
        duration,
        ease,
        stagger: delay / 1000,
        force3D: true,
      });
    })();

    return () => {
      try {
        tl?.kill();
        ScrollTrigger?.getAll()?.forEach((t: any) => t.kill());
        gsap?.killTweensOf?.(targets);
        splitter?.revert?.();
      } catch {
        /* noop */
      }
    };
  }, [
    text,
    delay,
    duration,
    ease,
    splitType,
    from,
    to,
    threshold,
    rootMargin,
    onLetterAnimationComplete,
  ]);

  return (
    <>
      {ariaSafe && <span className="sr-only">{text}</span>}
      {React.createElement(
        Comp as any,
        {
          ref,
          "aria-hidden": ariaSafe ? true : undefined,
          className: `split-parent overflow-hidden inline-block whitespace-normal ${className}`,
          style: { textAlign, wordWrap: "break-word" },
        },
        text
      )}
    </>
  );
};

export default SplitText;
